import { Injectable } from '@angular/core';
import { CRS } from 'leaflet';
import extend from 'lodash-es/extend';
import isObject from 'lodash-es/isObject';
import pick from 'lodash-es/pick';

import { ConfigService } from '@finder/core/config.service';
import { MapHelpers } from '@finder/shared/components/map/map.helpers';
import {
    finderMapSites,
    FINDER_MAP_BASE,
    FINDER_MAP_DEFAULT_URL
} from '@finder/shared/components/map/defaults/map.defaults.constants';

/**
 * @description Create a new class that handles defaults merged with passed in values
 *              wdpr-ui-angular-mapplication/src/map/mapplication-defaults.factory.js
 */
@Injectable()
export class MapDefaultsService {

    public defaults = {};

    constructor(
        private configService: ConfigService,
        private helper: MapHelpers
    ) { }

    /**
     * @name        getDefaults
     * @description Get default values for a given map
     * @param       scopeId Used to identify a unique map if there is more than one on a page
     * @returns Object
     */
    public getDefaults(scopeId) {
        const mapId = this.helper.obtainEffectiveMapId(this.defaults, scopeId);

        return this.defaults[mapId];
    }

    /**
     * @name        setDefaults
     * @description Configure the defaults to show the map
     * @param       userDefaults? Defaults from the user
     * @param       siteId? Identifies the site the map is used on
     * @param       scopeId? Used to identify a unique map if there is more than one on a page
     * @returns     Object
     */
    public setDefaults(userDefaults?, siteId?, scopeId?) {
        // Get the defaults based on the site name
        const newDefaults = this.getSiteDefaults(siteId);
        // Get the name of the map
        const mapId = this.helper.obtainEffectiveMapId(this.defaults, scopeId);

        // If we have any defaults passed in merge them with the new set of defaults
        if (userDefaults) {
            // Set the user defined events
            this.setDefaultEvents(newDefaults, userDefaults);

            // Set the user defined properties
            this.setDefaultsMap(newDefaults, userDefaults);
        }

        // Set the map defaults on the class
        this.defaults[mapId] = newDefaults;

        return newDefaults;
    }

    /**
     * @name        getMapCreationDefaults
     * @description Get default values for creating a map
     * @param       scopeId Used to identify a unique map if there is more than one on a page
     * @returns     Object
     */
    public getMapCreationDefaults(scopeId?) {
        const mapId = this.helper.obtainEffectiveMapId(this.defaults, scopeId);
        const defaults = this.defaults[mapId] || {};
        let mapDefaults;

        // Pick default values and assign them to a new object
        mapDefaults = pick(defaults, [
            'mapType',
            'maxZoom',
            'keyboard',
            'dragging',
            'zoomControl',
            'doubleClickZoom',
            'scrollWheelZoom',
            'touchZoom',
            'attributionControl',
            'worldCopyJump',
            'crs',
            'zoom',
            'preferCanvas',
            // The following vars are optional and doesn't break the copy if they don't exist
            'minZoom',
            'zoomAnimation',
            'fadeAnimation',
            'markerZoomAnimation',
            'zoomOptions',
            'zoomSnap'
        ]);

        // Pull defaults from the map object
        mapDefaults = extend({}, defaults.map, mapDefaults);

        return mapDefaults;
    }

    /**
     * @name        getSiteDefaults
     * @description Build a set of defaults based on the site passed in
     * @param       siteId? Identifies the site the map is used on
     * @returns     Object
     */
    private getSiteDefaults(siteId?) {
        let cdnPath = this.configService.getValue('finderMapBaseUrl');
        if (!cdnPath) {
            cdnPath = FINDER_MAP_DEFAULT_URL;
        }
        // Get the configs that are brand specific.
        const mapSites = finderMapSites(cdnPath);

        // Copy the base values. If a siteId is present then copy those values as well
        return extend({}, FINDER_MAP_BASE, siteId ? mapSites[siteId] : {});
    }

    /**
     * @name        setDefaultEvents
     * @description Map user events onto the default events object
     * @param       newDefaults Base default values
     * @param       userDefaults User default values
     */
    private setDefaultEvents(newDefaults, userDefaults) {
        const defaultMethods = [
            'doubleClickZoom',
            'scrollWheelZoom',
            'touchZoom',
            'zoomControl',
            'attributionControl',
            'tileLayer',
            'zoomControlPosition',
            'keyboard',
            'dragging'
        ];

        // Check the userDefaults for methods to override in the new defaults
        this.mergePropertyValues(newDefaults, userDefaults, defaultMethods);
    }

    /**
     * @name        setDefaultsMap
     * @description Map user properties onto the default properties object
     * @param       newDefaults Base default values
     * @param       userDefaults User default values
     */
    private setDefaultsMap(newDefaults, userDefaults) {
        const defaultProperties = [
            'controls',
            'crs', // Might reference the EPSG3857 value directly
            'center',
            'tileLayerOptions',
            'maxZoom',
            'minZoom',
            'zoom',
            'zoomAnimation',
            'fadeAnimation',
            'markerZoomAnimation',
            'addZoomControl',
            'addExpandControl',
            'addRecenterControl',
            'maxBounds',
            'zoomOptions',
            'zoomSnap'
        ];

        // Check the userDefaults for methods to override in the new defaults
        this.mergePropertyValues(newDefaults, userDefaults, defaultProperties);

        // Double check the crs value
        if (CRS[userDefaults['crs']]) {
            extend(newDefaults['crs'], CRS[userDefaults['crs']]);
        }

        // We don't want to clone the map. We just want to copy the reference
        if (userDefaults.map) {
            newDefaults.map = userDefaults.map;
        }
    }

    /**
     * @name        mergePropertyValues
     * @description Map the user defaults over the base default values
     * @param       newDefaults Base default values
     * @param       userDefaults User default values
     * @param       overloadValues Look for these values in the user defaults and overwrite them in the newDefaults
     */
    private mergePropertyValues(newDefaults, userDefaults, overloadValues) {
        // Loop over our user defined methods and add them to the new defaults
        Object.keys(userDefaults).forEach(key => {
            // If we don't need to do anything with the specified key then just stop
            if (overloadValues.indexOf(key) === -1) {
                return;
            }

            // If the overload value doesn't exist on newDefaults just move it over
            if (!newDefaults[key]) {
                newDefaults[key] = userDefaults[key];

                return;
            }

            // If we're overloading an object we need to extend it onto newDefaults
            if (isObject(userDefaults[key])) {
                extend(newDefaults[key], userDefaults[key]);
            } else {
                newDefaults[key] = userDefaults[key];
            }
        });
    }
}
